import type { HttpClient, RequestOptions } from '../client.js';
import {
  type PriceData,
  type PriceHistoryData,
  type TopPricesData,
  type TokenSearchData,
  type TokenAddressesData,
  getPriceParamsSchema,
  getPriceHistoryParamsSchema,
  getTopPricesParamsSchema,
  searchTokensParamsSchema,
  getTokenAddressesParamsSchema,
} from '@spectrum-nodes/api-spec/models';
import { parseParams } from '../internals/validation.js';

/** Token prices, price history, and token discovery */
export class PricesNamespace {
  constructor(private readonly client: HttpClient) {}

  /** Get current price for a token symbol. */
  async getPrice(symbol: string, options?: RequestOptions): Promise<PriceData> {
    const validated = parseParams(getPriceParamsSchema, { symbol });
    return this.client.jsonRpc<PriceData>('getPrice', validated, options);
  }

  /** Get price history for a token symbol. */
  async getPriceHistory(
    symbol: string,
    params?: { days?: number; currency?: string },
    options?: RequestOptions,
  ): Promise<PriceHistoryData> {
    const validated = parseParams(getPriceHistoryParamsSchema, {
      symbol,
      days: params?.days,
      currency: params?.currency,
    });
    return this.client.jsonRpc<PriceHistoryData>('getPriceHistory', validated, options);
  }

  /** Get top tokens by market cap. */
  async getTopPrices(
    params?: { limit?: number; currency?: string },
    options?: RequestOptions,
  ): Promise<TopPricesData> {
    const validated = parseParams(getTopPricesParamsSchema, {
      limit: params?.limit,
      currency: params?.currency,
    });
    return this.client.jsonRpc<TopPricesData>('getTopPrices', validated, options);
  }

  /** Search for tokens by name or symbol. */
  async searchTokens(
    query: string,
    params?: { limit?: number },
    options?: RequestOptions,
  ): Promise<TokenSearchData> {
    const validated = parseParams(searchTokensParamsSchema, { q: query, limit: params?.limit });
    return this.client.jsonRpc<TokenSearchData>('searchTokens', validated, options);
  }

  /** Get known contract addresses for a token symbol across chains. */
  async getTokenAddresses(symbol: string, options?: RequestOptions): Promise<TokenAddressesData> {
    const validated = parseParams(getTokenAddressesParamsSchema, { symbol });
    return this.client.jsonRpc<TokenAddressesData>('getTokenAddresses', validated, options);
  }
}
